// Angular
import {
	EventEmitter,
	Component,
	ComponentFactoryResolver,
	ElementRef,
	OnDestroy,
	OnInit,
	ViewChild,
	AfterViewInit
} from '@angular/core';
// Model
import { RuleModel } from '../rules/model/rule.model';
import { RuleEngine } from '../rules/model/rule-engine';
import { UISTATE, SCHEMA_TYPE, DOCUMENT_RULES_UI_SECTIONS } from '../model/quality.constant';
import { PHOTO_REVIEW_UISTATE } from '../photo-review/model/photo-review.constant';
import { OrderDetailModel } from '../state/model/order-detail.model';
import { PhotoReviewModel } from '../photo-review/model/photo-review.model';
import { QualityStateModel } from '../state/model/quality-state.model';
// State
import { Store } from '@ngxs/store';
import { RulesState } from '../rules/state/rules.state';
import { QualityState } from '../state/quality.state';
import { PhotoReviewState } from '../photo-review/state/photo-review.state';
import { DeleteSelectedRule } from '../rules/state/action/rules.actions';
// Component
import { HISTORICAL_VALUE_EVENT } from '../historical-values/historical-values.component';
// Service
import { SubContentService, BroadcastService } from '@cv-invision/core';
import { JPathService, PhotoGalleryService } from '@cv-invision/cv.component-library.ux';
// Other
import { Subject, Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
	selector: 'cv-overview',
	templateUrl: './overview.component.html',
	styleUrls: ['./overview.component.scss'],
	providers: [PhotoGalleryService]
})
export class OverviewComponent implements OnInit, AfterViewInit, OnDestroy {
	@ViewChild('contentOverview', { static: true }) contentOverview: ElementRef<any>;
	orderDetail$: Observable<OrderDetailModel> = this.store.select(QualityState.getOrderDetail);
	documentOrder$: Observable<QualityStateModel> = this.store.select(QualityState.getOrderDocument);
	photo$: Observable<Array<PhotoReviewModel>> = this.store.select(PhotoReviewState.getPhotoReviewCollection);
	selectedRule$: Observable<any> = this.store.select(RulesState.getSelectedRule);

	public uiState = UISTATE;
	public overviewUiState = UISTATE.IS_LOADING;
	public orderDetailModel: OrderDetailModel;
	public documentOrder: any;
	public photosCollection: any[] = [];
	public selectedRule: any;

	private overviewEvents = new Subject();
	private atComponentDestroy = new Subject();
	private scrollTop: number;
	private contentLoaded = {
		documentOrder: false,
		photos: false
	};

	constructor(
		private store: Store,
		public subContentService: SubContentService,
		public componentFactoryResolver: ComponentFactoryResolver,
		private broadcastService: BroadcastService,
		private jPathService: JPathService
	) {
		this.selectedRule = {
			target: '',
			type: ''
		};
	}

	ngOnInit(): void {
		this.photosCollection = [];
		RuleEngine.setSelectedSection(this.store, DOCUMENT_RULES_UI_SECTIONS.OVERVIEW);

		this.overviewEvents
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe((data: any) => {
				if ('contentName' in data && data.contentName !== '') {
					this.contentLoaded[data.contentName] = true;
				}

				let canActivateUI = false;
				const loaded: string[] = [];
				const totalContent = Object.keys(this.contentLoaded);
				totalContent.forEach(key => {
					if (this.contentLoaded[key]) {
						loaded.push(key);
					}
				})

				if (loaded.length === totalContent.length) {
					this.overviewUiState = UISTATE.IS_READY;
				}
			});

		this.overviewUiState = UISTATE.IS_LOADING;
		this.orderDetail$
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe((orderDetailModel: OrderDetailModel) => {
				if (orderDetailModel) {
					this.orderDetailModel = orderDetailModel;
				}
			});

		this.photo$.pipe(takeUntil(this.atComponentDestroy)).subscribe((pt: any) => {
			if (pt) {
				let photoCollection = pt;

				if (this.orderDetailModel.schema === SCHEMA_TYPE.INSPECTION_R) {
					photoCollection = this.getMainPhoto(pt);
				}

				photoCollection = photoCollection.map(photo => {
					const response = {
						description: '',
						medium: PHOTO_REVIEW_UISTATE.DEFAULT_IMG,
						big: PHOTO_REVIEW_UISTATE.DEFAULT_IMG,
						src: PHOTO_REVIEW_UISTATE.DEFAULT_IMG,
						thumb: PHOTO_REVIEW_UISTATE.DEFAULT_IMG
					}

					if (photo.thumbnails) {
						response.medium = photo.thumbnails[0] || photo.path;
						response.big = photo.path;
						response.src = photo.path;
						response.thumb = photo.thumbnails[0] || photo.path;
					}
					return response;
				});

				this.photosCollection = photoCollection;
				this.overviewEvents.next({contentName: 'photos'});
			}
		});

		this.documentOrder$
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe((documentOrder: QualityStateModel) => {
				if (documentOrder) {
					this.documentOrder = documentOrder;
					this.overviewEvents.next({contentName: 'documentOrder'});
				}
			});
	}

	ngAfterViewInit(): void {
		const el = this.contentOverview.nativeElement;
		if (el) {
			el.addEventListener('scroll', this.onScroll.bind(this), true);
		}

		// After a rule has been selected if it belongs to the same section it will try to Highlight the Field
		this.selectedRule$.pipe(takeUntil(this.atComponentDestroy)).subscribe((selectedRule: any) => {
			if (
				selectedRule &&
				RuleEngine.isCurrentUISection(selectedRule, DOCUMENT_RULES_UI_SECTIONS.OVERVIEW)
			) {
				setTimeout(() => {
					this.getSelectedRule(selectedRule);
					// We Clean latest value
					this.store.dispatch(new DeleteSelectedRule());
				});
			}
		});
	}

	/**
	 * get historical value base on doc path
	 * @param path
	 */
	hasHistorical(path: string): Array<any> {
		const field = this.jPathService.getValue(this.documentOrder, `$.${path}`);
		return field.Historical;
	}

	/**
	 * get the rule selected
	 * @param item selected rule model
	 */
	getSelectedRule(item: RuleModel): void {
		const targets = item.targets && item.targets.length ? item.targets : item.primaryTargets;
		if (targets.length) {
			this.selectedRule = {
				target: targets,
				type: `cv-overview-data__info__${item.messageType}`
			};
		} else {
			this.selectedRule = {};
		}
	}

	/**
	 * open historical component
	 * @param path
	 * @param ele
	 */
	historicalVal($event: any): void {
		const historical = this.hasHistorical($event.path);
		const el = $event.btns.filter(item => {
			return item.nativeElement === $event.ele;
		});
		const { offsetHeight, offsetLeft, offsetTop, offsetWidth } = el[0].nativeElement.parentNode;
		const scrollTop = this.scrollTop;
		this.broadcastService.broadcast({
			name: HISTORICAL_VALUE_EVENT,
			event: {
				isOpen: true,
				data: historical,
				pos: { offsetHeight, offsetLeft: offsetLeft - 25, offsetTop, offsetWidth, scrollTop }
			}
		});
	}

	/**
	 * get scroll event
	 * @param $event
	 */
	onScroll(event: any): void {
		const target = event.target as Element;
		const isHistoricalCmp = target.classList.contains('cv-historical-values__container');

		if (!isHistoricalCmp) {
			const { scrollTop } = event.target;
			this.scrollTop = scrollTop;
			this.broadcastService.broadcast({
				name: HISTORICAL_VALUE_EVENT,
				event: {
					isOpen: false,
					data: [],
					pos: {}
				}
			});
		}
	}

	/**
	 * get the rule selected
	 * @param item selected rule model
	 */
	getMainPhoto(photos: Array<any>): Array<any> {
		return photos.filter(
			(photo: any) => photo.metadata && photo.metadata.photoType === 'VIEW_FRONT'
		);
	}

	/**
	 * Close all when the element is destroyed
	 * in case a Sub Component remains visible
	 */
	ngOnDestroy(): void {
		// Globally Destroy anything attached to it
		this.atComponentDestroy.next({});
		this.atComponentDestroy.unsubscribe();
	}
}
